<template>
  <div>
    <breadcrumb :items="breadcrumbs" />
    <v-container fluid>
      <!-- Header -->
      <div class="d-flex align-center mb-6">
        <h2 class="font-weight-bolder">{{ isEditMode ? $t('finance.banks.editBank') : $t('finance.banks.addNewBank') }}</h2>
        <v-spacer></v-spacer>
        <v-btn outlined color="outline" class="mr-4" @click="cancel">
          {{ $t('finance.banks.cancel') }}
        </v-btn>
        <v-btn color="primary" @click="save">
          {{ $t('finance.banks.save') }}
        </v-btn>
      </div>

      <!-- Top Card: Bank Info -->
      <v-card elevation="0" class="mb-6 pa-6">
        <v-row>
          <v-col cols="12" md="6">
            <label class="font-weight-bold mb-2 d-block">{{ $t('finance.banks.bankTitle') }}</label>
            <v-text-field
              v-model="bank.title"
              :placeholder="$t('finance.banks.enterHere')"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6"></v-col>
          <v-col cols="12" md="6">
            <label class="font-weight-bold mb-2 d-block">{{ $t('finance.banks.attachBankLogo') }}</label>
            <div class="upload-area d-flex flex-column align-center justify-center py-8" @click="triggerUpload">
              <template v-if="logoPreview">
                <img :src="logoPreview" :alt="$t('finance.banks.bankLogoPreview')" class="logo-preview mb-3" />
                <v-btn color="primary" small depressed>
                  {{ $t('finance.banks.changeImage') }}
                </v-btn>
              </template>
              <template v-else>
                <v-icon size="48" color="grey lighten-1">mdi-camera-outline</v-icon>
                <v-btn color="primary" class="mt-4" small depressed>
                  {{ $t('finance.banks.addImage') }}
                </v-btn>
              </template>
              <input type="file" ref="fileInput" class="d-none" @change="handleFileUpload" accept="image/*">
            </div>
          </v-col>
          <v-col cols="12" md="6"></v-col>
        </v-row>
      </v-card>

      <!-- Bottom Card: Details -->
      <v-card elevation="0" class="pa-6">
        <!-- Contact Details -->
        <h3 class="font-weight-bold mb-4">{{ $t('finance.banks.contactDetails') }}</h3>
        <v-row>
          <v-col cols="12" md="3">
            <label class="font-weight-bold mb-2 d-block">{{ $t('finance.banks.firstName') }}</label>
            <v-text-field
              v-model="bank.firstName"
              :placeholder="$t('finance.banks.enterHere')"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <label class="font-weight-bold mb-2 d-block">{{ $t('finance.banks.lastName') }}</label>
            <v-text-field
              v-model="bank.lastName"
              :placeholder="$t('finance.banks.enterHere')"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <label class="font-weight-bold mb-2 d-block">{{ $t('finance.banks.email') }}</label>
            <v-text-field
              v-model="bank.email"
              :placeholder="$t('finance.banks.enterHere')"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <label class="font-weight-bold mb-2 d-block">{{ $t('finance.banks.phone') }}</label>
            <v-text-field
              v-model="bank.phone"
              :placeholder="$t('finance.banks.enterHere')"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <!-- API/Integration Info -->
        <h3 class="font-weight-bold mb-4">{{ $t('finance.banks.apiIntegrationInfo') }}</h3>
        <v-row>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class="font-weight-bold mr-1">{{ $t('finance.banks.apiBaseUrl') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.apiBaseUrlInfo') }}</span>
              </v-tooltip>
            </div>
            <v-text-field
              v-model="bank.apiBaseUrl"
              placeholder="https://api.dimitra.com/xx23x/loan"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class="font-weight-bold mr-1">{{ $t('finance.banks.clientId') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.clientIdInfo') }}</span>
              </v-tooltip>
            </div>
            <v-text-field
              v-model="bank.clientId"
              placeholder="TBA-SR-001"
              outlined
              dense
              hide-details="auto"
              background-color="grey lighten-4"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <div class="d-flex align-center mb-2">
              <label class="font-weight-bold mr-1">{{ $t('finance.banks.clientSecret') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.clientSecretInfo') }}</span>
              </v-tooltip>
            </div>
            <div class="d-flex align-center">
              <v-text-field
                v-model="bank.clientSecret"
                :type="showClientSecret ? 'text' : 'password'"
                outlined
                dense
                hide-details="auto"
                background-color="grey lighten-4"
                readonly
                class="mr-2"
              >
                <template v-slot:append>
                  <v-icon @click="showClientSecret = !showClientSecret">
                    {{ showClientSecret ? 'mdi-eye' : 'mdi-eye-off' }}
                  </v-icon>
                </template>
              </v-text-field>
              <v-btn small outlined color="primary" class="mr-2" @click="copyToClipboard(bank.clientSecret)">
                <v-icon left small>mdi-content-copy</v-icon>
                {{ $t('finance.banks.copy') }}
              </v-btn>
              <v-btn small outlined color="primary" @click="regenerateSecret">
                <v-icon left small>mdi-refresh</v-icon>
                {{ $t('finance.banks.regenerate') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <!-- Account/Admin Credentials -->
        <h3 class="font-weight-bold mb-4">{{ $t('finance.banks.accountAdminCredentials') }}</h3>
        <v-row>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class="font-weight-bold mr-1">{{ $t('finance.banks.adminUsername') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.adminUsernameInfo') }}</span>
              </v-tooltip>
            </div>
            <v-text-field
              v-model="bank.adminUsername"
              placeholder="admin@trustbank.com"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class="font-weight-bold mr-1">{{ $t('finance.banks.password') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.passwordInfo') }}</span>
              </v-tooltip>
            </div>
            <v-text-field
              v-model="bank.password"
              :type="showPassword ? 'text' : 'password'"
              outlined
              dense
              hide-details="auto"
              background-color="grey lighten-4"
            >
              <template v-slot:append>
                <v-icon @click="showPassword = !showPassword">
                  {{ showPassword ? 'mdi-eye' : 'mdi-eye-off' }}
                </v-icon>
              </template>
            </v-text-field>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <!-- Require 2FA -->
        <h3 class="font-weight-bold mb-4">{{ $t('finance.banks.require2FA') }}</h3>
        <div class="d-flex align-center">
          <span class="mr-4">{{ $t('finance.banks.enable') }}</span>
          <v-switch
            v-model="bank.require2FA"
            color="primary"
            inset
            hide-details
            class="mt-0 pt-0"
          ></v-switch>
        </div>

      </v-card>
    </v-container>
  </div>
</template>

<script>
import FinanceService from "@/_services/FinanceService";
export default {
  created() {
    this.isEditMode = !!this.$route.params.isEditMode;
    this.bankId = this.$route.params.id || null;
    if (this.isEditMode && this.bankId) {
      this.breadcrumbs[1].text = this.$t('finance.banks.editBank');
      this.fetchBankDetails();
    }
  },
  data() {
    return {
      isEditMode: false,
      bankId: null,
      breadcrumbs: [
        {
          text: this.$t("finance.banks.title"),
          disabled: false,
          href: "/finance-management/banks",
        },
        {
          text: this.$t("finance.banks.addNewBank"),
          disabled: true,
        },
      ],
      bank: {
        title: '',
        description: '',
        logo: null,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        apiBaseUrl: '',
        clientId: '',
        clientSecret: '',
        adminUsername: '',
        password: '',
        require2FA: true,
      },
      logoPreview: null,
      showClientSecret: false,
      showPassword: false,
    };
  },
  methods: {
    cancel() {
      this.$router.go(-1);
    },
    async fetchBankDetails() {
      try {
        const response = await FinanceService.getBanksById(this.bankId);
        const d = response.data;
        if (d) {
          this.bank = {
            title: d.name || '',
            description: d.description || '',
            logo: null,
            firstName: d.contactFirstName || '',
            lastName: d.contactLastName || '',
            email: d.contactEmail || '',
            phone: d.contactPhoneNo || '',
            apiBaseUrl: d.apiBaseUrl || '',
            clientId: d.apiClientId || '',
            clientSecret: d.apiClientSecret || '',
            adminUsername: d.adminUsername || '',
            password: d.adminPasswd || '',
            require2FA: !!d.require2fa,
          };
          if (d.logo) {
            this.logoPreview = d.logo;
          }
        }
      } catch (err) {
        console.error('Failed to load bank details:', err);
        this.$notify({ title: this.$t('finance.banks.failedToLoadBankDetails'), type: 'error' });
      }
    },
    async save() {
      try {
        const payload = new FormData();
        payload.append('name', this.bank.title);
        payload.append('description', this.bank.description);
        if (this.bank.logo) {
          payload.append('logo', this.bank.logo);
        }
        payload.append('contactFirstName', this.bank.firstName);
        payload.append('contactLastName', this.bank.lastName);
        payload.append('contactEmail', this.bank.email);
        payload.append('contactPhoneNo', this.bank.phone);
        payload.append('apiBaseUrl', this.bank.apiBaseUrl);
        payload.append('apiClientId', this.bank.clientId);
        payload.append('apiClientSecret', this.bank.clientSecret);
        payload.append('adminUsername', this.bank.adminUsername);
        payload.append('adminPasswd', this.bank.password);
        payload.append('require2fa', this.bank.require2FA);

        if (this.isEditMode) {
          await FinanceService.updateBank(this.bankId, payload);
          this.$notify({ title: this.$t('finance.banks.bankUpdatedSuccessfully'), type: 'success' });
        } else {
          await FinanceService.createBank(payload);
          this.$notify({ title: this.$t('finance.banks.bankCreatedSuccessfully'), type: 'success' });
        }
        this.$router.push({ name: 'banks' });
      } catch (error) {
        this.$notify({
          title: this.isEditMode
            ? this.$t('finance.banks.errorUpdatingBank')
            : this.$t('finance.banks.errorCreatingBank'),
          type: 'error',
        });
      }
    },
    triggerUpload() {
      this.$refs.fileInput.click();
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.bank.logo = file;
        this.logoPreview = URL.createObjectURL(file);
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
      // Optional: Show a snackbar or toast
    },
    regenerateSecret() {
      // Implement regeneration logic
      this.bank.clientSecret = Math.random().toString(36).slice(-16);
    },
  },
};
</script>

<style scoped>
.upload-area {
  border: 1px dashed #ccc;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.upload-area:hover {
  background-color: #f9f9f9;
}

.logo-preview {
  max-width: 120px;
  max-height: 120px;
  object-fit: contain;
  border-radius: 4px;
}
</style>